<template>
  <div class="login-page">
    <div class="container">
      <div class="auth-container">
        <div class="auth-card card">
          <div class="auth-header">
            <h1 class="auth-title">{{ isLogin ? $t('auth.login') : $t('auth.register') }}</h1>
            <p class="auth-subtitle">{{ isLogin ? $t('auth.welcomeBack') : $t('auth.createAccount') }}</p>
          </div>
          
          <div class="auth-tabs">
            <button 
              class="auth-tab" 
              :class="{ active: isLogin }" 
              @click="isLogin = true"
            >
              {{ $t('auth.login') }}
            </button>
            <button 
              class="auth-tab" 
              :class="{ active: !isLogin }" 
              @click="isLogin = false; checkTermsDialog()"
            >
              {{ $t('auth.register') }}
            </button>
          </div>
          
          <form class="auth-form" @submit.prevent="handleSubmit">
            <div class="form-group" v-if="!isLogin">
              <label class="form-label">{{ $t('auth.username') }}</label>
              <input 
                type="text" 
                class="form-input" 
                v-model="username" 
                :placeholder="$t('auth.usernamePlaceholder')"
                required
              />
            </div>
            
            <div class="form-group" v-if="isLogin && !twoFactorChallengeId">
              <label class="form-label">{{ $t('auth.username') }}</label>
              <input 
                type="text" 
                class="form-input" 
                v-model="username" 
                :placeholder="$t('auth.usernamePlaceholder')"
                required
              />
            </div>
            
            <div class="form-group" v-if="!isLogin">
              <label class="form-label">{{ $t('auth.inviteCode') }}</label>
              <input 
                type="text" 
                class="form-input" 
                v-model="inviterCode" 
                :placeholder="$t('auth.inviteCodePlaceholder')"
              />
            </div>
            
            <div class="form-group" v-if="!isLogin || !twoFactorChallengeId">
              <label class="form-label">{{ isLogin ? $t('auth.password') : $t('auth.setPassword') }}</label>
              <div class="password-input-container">
                <input 
                  :type="showPassword ? 'text' : 'password'" 
                  class="form-input" 
                  v-model="password" 
                  :placeholder="$t('auth.passwordPlaceholder')"
                  required
                />
                <button 
                  type="button" 
                  class="password-toggle" 
                  @click="showPassword = !showPassword"
                >
                  <svg v-if="showPassword" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9.9 4.24002C10.5883 4.0789 11.2931 3.99834 12 4.00002C19 4.00002 22 12 22 12C21.393 13.1356 20.6691 14.2048 19.84 15.19" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M14.12 14.12C13.8454 14.4148 13.5141 14.6512 13.1462 14.8151C12.7782 14.9791 12.3809 15.0673 11.9781 15.0744C11.5753 15.0815 11.1752 15.0074 10.8016 14.8565C10.4281 14.7056 10.0887 14.4811 9.80385 14.1962C9.51897 13.9113 9.29439 13.572 9.14351 13.1984C8.99262 12.8249 8.91853 12.4247 8.92563 12.0219C8.93274 11.6191 9.02091 11.2219 9.18488 10.8539C9.34884 10.4859 9.58525 10.1547 9.88 9.88002" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M2.00001 2.00002L22 22" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M2.00001 12C2.00001 12 5.00001 4.00002 12 4.00002C13.0405 3.99608 14.0756 4.14677 15.07 4.44002" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M12 19C11.1346 19.0067 10.2733 18.8773 9.45 18.62C7.78 18.15 6.35 17.27 5.21 16.15C4.41276 15.3804 3.71139 14.5172 3.12 13.58C2.99 13.39 2.87 13.19 2.76 13C2.68 12.86 2.62 12.74 2.57 12.62" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  <svg v-else width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 5C5.63636 5 2 12 2 12C2 12 5.63636 19 12 19C18.3636 19 22 12 22 12C22 12 18.3636 5 12 5Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </button>
              </div>
            </div>

            <div class="form-group" v-if="isLogin && twoFactorChallengeId">
              <label class="form-label">{{ $t('auth.twoFactorCode') }}</label>
              <input
                class="form-input"
                v-model.trim="twoFactorCode"
                inputmode="numeric"
                autocomplete="one-time-code"
                maxlength="6"
                :placeholder="$t('auth.twoFactorCodePlaceholder')"
                required
              />
              <p class="auth-subtitle">{{ $t('auth.twoFactorPrompt') }}</p>
            </div>
            
            <div class="form-group" v-if="!isLogin">
              <label class="form-label">{{ $t('auth.confirmPassword') }}</label>
              <div class="password-input-container">
                <input 
                  :type="showConfirmPassword ? 'text' : 'password'" 
                  class="form-input" 
                  v-model="confirmPassword" 
                  :placeholder="$t('auth.confirmPasswordPlaceholder')"
                  required
                />
                <button 
                  type="button" 
                  class="password-toggle" 
                  @click="showConfirmPassword = !showConfirmPassword"
                >
                  <svg v-if="showConfirmPassword" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9.9 4.24002C10.5883 4.0789 11.2931 3.99834 12 4.00002C19 4.00002 22 12 22 12C21.393 13.1356 20.6691 14.2048 19.84 15.19" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M14.12 14.12C13.8454 14.4148 13.5141 14.6512 13.1462 14.8151C12.7782 14.9791 12.3809 15.0673 11.9781 15.0744C11.5753 15.0815 11.1752 15.0074 10.8016 14.8565C10.4281 14.7056 10.0887 14.4811 9.80385 14.1962C9.51897 13.9113 9.29439 13.572 9.14351 13.1984C8.99262 12.8249 8.91853 12.4247 8.92563 12.0219C8.93274 11.6191 9.02091 11.2219 9.18488 10.8539C9.34884 10.4859 9.58525 10.1547 9.88 9.88002" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M2.00001 2.00002L22 22" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M2.00001 12C2.00001 12 5.00001 4.00002 12 4.00002C13.0405 3.99608 14.0756 4.14677 15.07 4.44002" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M12 19C11.1346 19.0067 10.2733 18.8773 9.45 18.62C7.78 18.15 6.35 17.27 5.21 16.15C4.41276 15.3804 3.71139 14.5172 3.12 13.58C2.99 13.39 2.87 13.19 2.76 13C2.68 12.86 2.62 12.74 2.57 12.62" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  <svg v-else width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 5C5.63636 5 2 12 2 12C2 12 5.63636 19 12 19C18.3636 19 22 12 22 12C22 12 18.3636 5 12 5Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </button>
              </div>
            </div>
            
            <!-- 添加用户协议复选框（仅注册时显示） -->
            <div class="form-group agreement-group" v-if="!isLogin">
              <div class="checkbox-wrapper">
                <input type="checkbox" id="agree-terms" v-model="agreedToTerms" required />
                <label for="agree-terms" class="agreement-label">
                  {{ $t('auth.agreeToTerms') }}
                  <a href="#" @click.prevent="showTermsDialog = true">{{ $t('auth.userAgreement') }}</a>
                  {{ $t('auth.connector.and') }}
                  <a href="#" @click.prevent="showPrivacyDialog = true">{{ $t('auth.privacyPolicy') }}</a>
                </label>
              </div>
            </div>

            <div v-if="turnstileEnabled && !twoFactorChallengeId" class="form-group turnstile-group">
              <div ref="turnstileContainerRef" class="turnstile-widget"></div>
              <p v-if="turnstileError" class="turnstile-error">{{ turnstileError }}</p>
            </div>
            
            <div class="form-actions">
              <button type="submit" class="btn btn-primary btn-full" :disabled="loading || (!isLogin && !agreedToTerms)">
                {{ loading ? $t('common.processing') : (isLogin ? $t('auth.login') : $t('auth.register')) }}
              </button>
            </div>
          </form>
          
          <!-- <div class="auth-divider">
            <span>或</span>
          </div>
          
          <div class="social-login">
            <button class="social-btn">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" fill="#F5F5F5"/>
                <path d="M17.6 12.1999H12.6V14.3999H15.7C15.5 15.2999 14.6 16.7999 12.6 16.7999C10.9 16.7999 9.5 15.3999 9.5 13.5999C9.5 11.7999 10.9 10.3999 12.6 10.3999C13.5 10.3999 14.1 10.7999 14.5 11.1999L16.2 9.5999C15.1 8.5999 13.9 7.9999 12.6 7.9999C9.5 7.9999 7 10.4999 7 13.5999C7 16.6999 9.5 19.1999 12.6 19.1999C15.8 19.1999 18 16.8999 18 13.6999C18 13.2999 17.7 12.4999 17.6 12.1999Z" fill="#4285F4"/>
              </svg>
              <span>谷歌账号登录</span>
            </button>
            <button class="social-btn">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M18.3701 22H5.62988C3.73291 22 2.18994 20.457 2.18994 18.56V5.44C2.18994 3.543 3.73291 2 5.62988 2H18.3701C20.2671 2 21.8101 3.543 21.8101 5.44V18.56C21.8101 20.457 20.2671 22 18.3701 22Z" fill="#3C5A99"/>
                <path d="M17 11.9999H14V8.99989H17V5.99989H14C12.3431 5.99989 11 7.34304 11 8.99989V11.9999H8V14.9999H11V22.9999H14V14.9999H16.5L17 11.9999Z" fill="white"/>
              </svg>
              <span>Facebook登录</span>
            </button>
          </div> -->
        </div>
      </div>
    </div>
    
    <!-- 用户服务协议对话框 -->
    <el-dialog
      v-model="showTermsDialog"
      :title="$t('auth.userAgreementTitle')"
      width="80%"
      :close-on-click-modal="false"
      class="terms-dialog"
    >
      <div class="terms-content" ref="termsContentRef" @scroll="handleTermsScroll" :data-scroll-hint="$t('auth.readFullAgreement')">
        <h2 align="center">{{ $t('auth.userAgreementTitle') }}</h2>
        
        <p>{{ $t('auth.userAgreementContent.welcome') }}</p>
        
        <h3>{{ $t('auth.userAgreementContent.serviceDescription.title') }}</h3>
        <p>{{ $t('auth.userAgreementContent.serviceDescription.content1') }}</p>
        <p>{{ $t('auth.userAgreementContent.serviceDescription.content2') }}</p>
        <p>{{ $t('auth.userAgreementContent.serviceDescription.content3') }}</p>
        
        <h3>{{ $t('auth.userAgreementContent.usageRestrictions.title') }}</h3>
        <p>{{ $t('auth.userAgreementContent.usageRestrictions.content1') }}</p>
        <p>{{ $t('auth.userAgreementContent.usageRestrictions.content2') }}</p>
        <ul>
          <li>{{ $t('auth.userAgreementContent.usageRestrictions.risks[0]') }}</li>
          <li>{{ $t('auth.userAgreementContent.usageRestrictions.risks[1]') }}</li>
          <li>{{ $t('auth.userAgreementContent.usageRestrictions.risks[2]') }}</li>
          <li>{{ $t('auth.userAgreementContent.usageRestrictions.risks[3]') }}</li>
          <li>{{ $t('auth.userAgreementContent.usageRestrictions.risks[4]') }}</li>
          <li>{{ $t('auth.userAgreementContent.usageRestrictions.risks[5]') }}</li>
          <li>{{ $t('auth.userAgreementContent.usageRestrictions.risks[6]') }}</li>
        </ul>
        
        <h3>{{ $t('auth.userAgreementContent.disclaimer.title') }}</h3>
        <p>{{ $t('auth.userAgreementContent.disclaimer.content1') }}</p>
        <p>{{ $t('auth.userAgreementContent.disclaimer.content2') }}</p>
        <p>{{ $t('auth.userAgreementContent.disclaimer.content3') }}</p>
        <p>{{ $t('auth.userAgreementContent.disclaimer.content4') }}</p>

        <h3>{{ $t('auth.userAgreementContent.userObligations.title') }}</h3>
        <p>{{ $t('auth.userAgreementContent.userObligations.content1') }}</p>
        <p>{{ $t('auth.userAgreementContent.userObligations.content2') }}</p>
        <p>{{ $t('auth.userAgreementContent.userObligations.content3') }}</p>
        
        <h3>{{ $t('auth.userAgreementContent.accountManagement.title') }}</h3>
        <p>{{ $t('auth.userAgreementContent.accountManagement.content1') }}</p>
        <p>{{ $t('auth.userAgreementContent.accountManagement.content2') }}</p>
        
        <h3>{{ $t('auth.userAgreementContent.agreementModification.title') }}</h3>
        <p>{{ $t('auth.userAgreementContent.agreementModification.content1') }}</p>
        <p>{{ $t('auth.userAgreementContent.agreementModification.content2') }}</p>
        
        <h3>{{ $t('auth.userAgreementContent.legalApplication.title') }}</h3>
        <p>{{ $t('auth.userAgreementContent.legalApplication.content1') }}</p>
        <p>{{ $t('auth.userAgreementContent.legalApplication.content2') }}</p>
      </div>
      
      <template #footer>
        <div class="dialog-footer">
          <el-button type="primary" @click="acceptTerms" :disabled="!hasReadTerms">
            {{ $t('auth.agreeButton') }}
          </el-button>
          <el-button @click="showTermsDialog = false">{{ $t('common.close') }}</el-button>
        </div>
      </template>
    </el-dialog>
    
    <!-- 隐私政策对话框 -->
    <el-dialog
      v-model="showPrivacyDialog"
      :title="$t('auth.privacyPolicyTitle')"
      width="80%"
      :close-on-click-modal="false"
      class="terms-dialog"
    >
      <div class="terms-content" ref="privacyContentRef" @scroll="handlePrivacyScroll" :data-scroll-hint="$t('auth.readFullAgreement')">
        <h2 align="center">{{ $t('auth.privacyPolicyTitle') }}</h2>
        
        <p>{{ $t('auth.privacyPolicyContent.welcome') }}</p>
        
        <h3>{{ $t('auth.privacyPolicyContent.informationCollection.title') }}</h3>
        <p>{{ $t('auth.privacyPolicyContent.informationCollection.content') }}</p>
        <ul>
          <li>{{ $t('auth.privacyPolicyContent.informationCollection.items[0]') }}</li>
          <li>{{ $t('auth.privacyPolicyContent.informationCollection.items[1]') }}</li>
          <li>{{ $t('auth.privacyPolicyContent.informationCollection.items[2]') }}</li>
          <li>{{ $t('auth.privacyPolicyContent.informationCollection.items[3]') }}</li>
        </ul>
        
        <h3>{{ $t('auth.privacyPolicyContent.informationUsage.title') }}</h3>
        <p>{{ $t('auth.privacyPolicyContent.informationUsage.content') }}</p>
        <ul>
          <li>{{ $t('auth.privacyPolicyContent.informationUsage.items[0]') }}</li>
          <li>{{ $t('auth.privacyPolicyContent.informationUsage.items[1]') }}</li>
          <li>{{ $t('auth.privacyPolicyContent.informationUsage.items[2]') }}</li>
          <li>{{ $t('auth.privacyPolicyContent.informationUsage.items[3]') }}</li>
          <li>{{ $t('auth.privacyPolicyContent.informationUsage.items[4]') }}</li>
        </ul>
        
        <h3>{{ $t('auth.privacyPolicyContent.informationSharing.title') }}</h3>
        <p>{{ $t('auth.privacyPolicyContent.informationSharing.content') }}</p>
        <ul>
          <li>{{ $t('auth.privacyPolicyContent.informationSharing.items[0]') }}</li>
          <li>{{ $t('auth.privacyPolicyContent.informationSharing.items[1]') }}</li>
          <li>{{ $t('auth.privacyPolicyContent.informationSharing.items[2]') }}</li>
          <li>{{ $t('auth.privacyPolicyContent.informationSharing.items[3]') }}</li>
        </ul>
        
        <h3>{{ $t('auth.privacyPolicyContent.informationSecurity.title') }}</h3>
        <p>{{ $t('auth.privacyPolicyContent.informationSecurity.content1') }}</p>
        <p>{{ $t('auth.privacyPolicyContent.informationSecurity.content2') }}</p>
        
        <h3>{{ $t('auth.privacyPolicyContent.userRights.title') }}</h3>
        <p>{{ $t('auth.privacyPolicyContent.userRights.content') }}</p>
        <ul>
          <li>{{ $t('auth.privacyPolicyContent.userRights.items[0]') }}</li>
          <li>{{ $t('auth.privacyPolicyContent.userRights.items[1]') }}</li>
          <li>{{ $t('auth.privacyPolicyContent.userRights.items[2]') }}</li>
        </ul>
        
        <h3>{{ $t('auth.privacyPolicyContent.cookiesAndTechnology.title') }}</h3>
        <p>{{ $t('auth.privacyPolicyContent.cookiesAndTechnology.content') }}</p>
        
        <h3>{{ $t('auth.privacyPolicyContent.policyUpdate.title') }}</h3>
        <p>{{ $t('auth.privacyPolicyContent.policyUpdate.content1') }}</p>
        <p>{{ $t('auth.privacyPolicyContent.policyUpdate.content2') }}</p>
        
        <h3>{{ $t('auth.privacyPolicyContent.contactUs.title') }}</h3>
        <p>{{ $t('auth.privacyPolicyContent.contactUs.content') }}</p>
      </div>
      
      <template #footer>
        <div class="dialog-footer">
          <el-button type="primary" @click="acceptPrivacy" :disabled="!hasReadPrivacy">
            {{ $t('auth.agreeButton') }}
          </el-button>
          <el-button @click="showPrivacyDialog = false">{{ $t('common.close') }}</el-button>
        </div>
      </template>
    </el-dialog>

  </div>
</template>

<script>
import { ref, inject, onMounted, onBeforeUnmount, nextTick, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { ElMessage } from 'element-plus'
import { getTurnstileConfig, login, verifyTwoFactorLogin, register, saveUserInfo } from '@/api/user'

const TURNSTILE_SCRIPT_ID = 'cloudflare-turnstile-script'
const TURNSTILE_SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
const TURNSTILE_SITE_KEY = process.env.VUE_APP_TURNSTILE_SITE_KEY || '0x4AAAAAAD7ue4HfHe2VweIj'
const TURNSTILE_SCRIPT_TIMEOUT = 15000
const TURNSTILE_MAX_AUTO_RETRIES = 2
const TURNSTILE_RETRY_DELAY = 2000
const TURNSTILE_NON_RETRYABLE_ERRORS = new Set(['110100', '110110', '110200', '400020', '400070'])
let turnstileLoadPromise = null

const loadTurnstileScript = () => {
  if (window.turnstile) {
    return Promise.resolve()
  }

  if (turnstileLoadPromise) {
    return turnstileLoadPromise
  }

  const existingScript = document.getElementById(TURNSTILE_SCRIPT_ID)
  if (existingScript) {
    existingScript.remove()
  }

  turnstileLoadPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.id = TURNSTILE_SCRIPT_ID
    script.src = TURNSTILE_SCRIPT_URL
    script.async = true
    script.defer = true
    const timeoutId = window.setTimeout(() => reject(new Error('Turnstile script load timeout')), TURNSTILE_SCRIPT_TIMEOUT)
    script.onload = () => {
      window.clearTimeout(timeoutId)
      if (window.turnstile) {
        resolve()
      } else {
        reject(new Error('Turnstile API is unavailable after script load'))
      }
    }
    script.onerror = error => {
      window.clearTimeout(timeoutId)
      reject(error)
    }
    document.head.appendChild(script)
  }).catch(error => {
    turnstileLoadPromise = null
    document.getElementById(TURNSTILE_SCRIPT_ID)?.remove()
    throw error
  })

  return turnstileLoadPromise
}

export default {
  name: 'LoginView',
  setup() {
    const router = useRouter()
    const route = useRoute()
    const { t: $t } = useI18n()

    // 获取全局登录状态更新方法
    const updateGlobalLoginStatus = inject('updateGlobalLoginStatus', null)

    const isLogin = ref(true)
    const username = ref('')
    const nickname = ref('')
    const password = ref('')
    const confirmPassword = ref('')
    const inviterCode = ref('')
    const showPassword = ref(false)
    const showConfirmPassword = ref(false)
    const loading = ref(false)
    const twoFactorChallengeId = ref('')
    const twoFactorCode = ref('')

    // Cloudflare Turnstile 人机验证
    const turnstileContainerRef = ref(null)
    const turnstileWidgetId = ref(null)
    const turnstileToken = ref('')
    const turnstileError = ref('')
    const turnstileEnabled = ref(false)
    const turnstileAction = ref('')
    const turnstileRetryCount = ref(0)
    const turnstileRetryTimer = ref(null)

    const clearTurnstileRetryTimer = () => {
      if (turnstileRetryTimer.value !== null) {
        window.clearTimeout(turnstileRetryTimer.value)
        turnstileRetryTimer.value = null
      }
    }

    const resetTurnstile = () => {
      turnstileToken.value = ''
      if (window.turnstile && turnstileWidgetId.value !== null) {
        window.turnstile.reset(turnstileWidgetId.value)
      }
    }

    const loadTurnstileConfig = async () => {
      try {
        const response = await getTurnstileConfig()
        turnstileEnabled.value = response.data?.enabled === true
      } catch (error) {
        console.error('获取 Turnstile 配置失败:', error)
        turnstileEnabled.value = false
      }
    }

    const retryTurnstile = () => {
      clearTurnstileRetryTimer()
      if (turnstileRetryCount.value >= TURNSTILE_MAX_AUTO_RETRIES) {
        turnstileError.value = $t('auth.messages.turnstileRetryExhausted')
        return
      }

      turnstileRetryCount.value += 1
      turnstileError.value = $t('auth.messages.turnstileRetrying')
      turnstileRetryTimer.value = window.setTimeout(() => {
        turnstileRetryTimer.value = null
        if (window.turnstile && turnstileWidgetId.value !== null) {
          resetTurnstile()
        } else {
          renderTurnstile()
        }
      }, TURNSTILE_RETRY_DELAY)
    }

    const renderTurnstile = async () => {
      if (!turnstileEnabled.value) {
        return
      }

      if (!TURNSTILE_SITE_KEY) {
        turnstileError.value = $t('auth.messages.turnstileConfigMissing')
        return
      }

      await nextTick()
      if (!turnstileContainerRef.value) {
        return
      }

      try {
        await loadTurnstileScript()
        turnstileError.value = ''

        const action = isLogin.value ? 'login' : 'register'

        if (turnstileWidgetId.value !== null && turnstileAction.value === action && window.turnstile) {
          window.turnstile.reset(turnstileWidgetId.value)
          return
        }

        if (turnstileWidgetId.value !== null && window.turnstile && typeof window.turnstile.remove === 'function') {
          window.turnstile.remove(turnstileWidgetId.value)
        } else if (turnstileContainerRef.value) {
          turnstileContainerRef.value.innerHTML = ''
        }

        turnstileWidgetId.value = null
        turnstileAction.value = action
        turnstileToken.value = ''

        turnstileWidgetId.value = window.turnstile.render(turnstileContainerRef.value, {
          sitekey: TURNSTILE_SITE_KEY,
          action,
          retry: 'never',
          'refresh-expired': 'manual',
          'refresh-timeout': 'manual',
          callback: token => {
            turnstileToken.value = token
            turnstileError.value = ''
            turnstileRetryCount.value = 0
            clearTurnstileRetryTimer()
          },
          'expired-callback': () => {
            turnstileToken.value = ''
            retryTurnstile()
          },
          'timeout-callback': () => {
            turnstileToken.value = ''
            retryTurnstile()
          },
          'error-callback': errorCode => {
            turnstileToken.value = ''
            console.error('Turnstile验证失败:', errorCode)
            if (errorCode === '110200') {
              turnstileError.value = $t('auth.messages.turnstileDomainUnauthorized')
              return
            }
            if (TURNSTILE_NON_RETRYABLE_ERRORS.has(errorCode)) {
              turnstileError.value = $t('auth.messages.turnstileConfigError')
              return
            }
            retryTurnstile()
          }
        })
      } catch (error) {
        console.error('Turnstile加载失败:', error)
        retryTurnstile()
      }
    }
    
    // 用户协议相关
    const agreedToTerms = ref(false)
    const showTermsDialog = ref(false)
    const showPrivacyDialog = ref(false)
    const hasShownTerms = ref(false)
    const hasReadTerms = ref(false)
    const hasReadPrivacy = ref(false)
    const termsContentRef = ref(null)
    const privacyContentRef = ref(null)
    
    // 检查是否已显示过用户协议
    const checkTermsDialog = () => {
      if (!hasShownTerms.value) {
        showTermsDialog.value = true
        hasShownTerms.value = true
        // 重置协议阅读状态
        hasReadTerms.value = false
        
        // 等待DOM更新后设置滚动监听
        nextTick(() => {
          if (termsContentRef.value) {
            termsContentRef.value.scrollTop = 0
          }
        })
      }
    }
    
    // 处理服务协议滚动
    const handleTermsScroll = (e) => {
      const element = e.target
      // 检查是否滚动到底部（允许5px的误差）
      if (element.scrollHeight - element.scrollTop - element.clientHeight < 5) {
        hasReadTerms.value = true
      }
    }
    
    // 处理隐私政策滚动
    const handlePrivacyScroll = (e) => {
      const element = e.target
      // 检查是否滚动到底部（允许5px的误差）
      if (element.scrollHeight - element.scrollTop - element.clientHeight < 5) {
        hasReadPrivacy.value = true
      }
    }
    
    // 接受用户协议
    const acceptTerms = () => {
      if (hasReadTerms.value) {
        agreedToTerms.value = true
        showTermsDialog.value = false
      }
    }
    
    // 接受隐私政策
    const acceptPrivacy = () => {
      if (hasReadPrivacy.value) {
        showPrivacyDialog.value = false
      }
    }
    
    // 显示隐私政策
    const showPrivacy = () => {
      showPrivacyDialog.value = true
      hasReadPrivacy.value = false
      
      // 等待DOM更新后重置滚动位置
      nextTick(() => {
        if (privacyContentRef.value) {
          privacyContentRef.value.scrollTop = 0
        }
      })
    }
    
    // 在组件挂载时，如果直接访问注册界面，显示用户协议
    // 同时检查URL参数中是否有邀请码
    onMounted(async () => {
      // 从URL参数中获取邀请码
      if (route.query.inviterCode) {
        inviterCode.value = route.query.inviterCode
        // 如果有邀请码，自动切换到注册页面
        isLogin.value = false
      }
      
      await loadTurnstileConfig()
      renderTurnstile()

      if (!isLogin.value) {
        checkTermsDialog()
      }
    })

    watch(isLogin, () => {
      twoFactorChallengeId.value = ''
      twoFactorCode.value = ''
      clearTurnstileRetryTimer()
      turnstileRetryCount.value = 0
      turnstileToken.value = ''
      turnstileError.value = ''
      renderTurnstile()
    })

    onBeforeUnmount(() => {
      clearTurnstileRetryTimer()
      if (window.turnstile && turnstileWidgetId.value !== null && typeof window.turnstile.remove === 'function') {
        window.turnstile.remove(turnstileWidgetId.value)
      }
    })
    
    // 监听协议对话框关闭事件
    watch(showTermsDialog, (newVal) => {
      if (!newVal && !agreedToTerms.value) {
        // 如果关闭对话框但未同意条款，重置已读状态
        hasReadTerms.value = false
      }
    })
    
    const handleSubmit = async () => {
      if (isLogin.value) {
        // 登录逻辑
        if ((!twoFactorChallengeId.value && (!username.value || !password.value))
          || (twoFactorChallengeId.value && !/^\d{6}$/.test(twoFactorCode.value))) {
          ElMessage.error($t('auth.messages.fillComplete'))
          return
        }

        if (!twoFactorChallengeId.value && turnstileEnabled.value && !turnstileToken.value) {
          ElMessage.error($t('auth.messages.turnstileRequired'))
          renderTurnstile()
          return
        }

        try {
          loading.value = true
          const res = twoFactorChallengeId.value
            ? await verifyTwoFactorLogin(twoFactorChallengeId.value, twoFactorCode.value)
            : await login(username.value, password.value, turnstileToken.value)

          if (res.success) {
            if (res.data?.twoFactorRequired) {
              twoFactorChallengeId.value = res.data.challengeId
              twoFactorCode.value = ''
              ElMessage.info($t('auth.twoFactorPrompt'))
              return
            }
            // 保存用户信息和token
            const userData = res.data
            const token = userData.token

            // 保存到localStorage
            saveUserInfo(userData, token)

            // 更新全局登录状态
            if (updateGlobalLoginStatus) {
              updateGlobalLoginStatus()
            }

            ElMessage.success($t('auth.messages.loginSuccess'))
            router.push('/')
          }
        } catch (error) {
          resetTurnstile()
          console.error($t('auth.errors.loginFailed'), error)
        } finally {
          loading.value = false
        }
      } else {
        // 注册逻辑
        if (!username.value || !password.value || !confirmPassword.value) {
          ElMessage.error($t('auth.messages.fillComplete'))
          return
        }

        if (password.value !== confirmPassword.value) {
          ElMessage.error($t('auth.messages.passwordMismatch'))
          return
        }

        if (!agreedToTerms.value) {
          // 如果未同意协议，则显示协议对话框
          showTermsDialog.value = true
          ElMessage.warning($t('auth.messages.agreeRequired'))
          return
        }

        if (turnstileEnabled.value && !turnstileToken.value) {
          ElMessage.error($t('auth.messages.turnstileRequired'))
          renderTurnstile()
          return
        }

        try {
          loading.value = true
          const res = await register(
            username.value,
            username.value,
            password.value,
            null, // mobile
            inviterCode.value || null,
            turnstileToken.value
          )

          if (res.success) {
            ElMessage.success($t('auth.messages.registerSuccess'))
            isLogin.value = true
            password.value = ''
            confirmPassword.value = ''
          }
        } catch (error) {
          resetTurnstile()
          console.error($t('auth.errors.registerFailed'), error)
        } finally {
          loading.value = false
        }
      }
    }
    
    return {
      isLogin,
      username,
      nickname,
      password,
      confirmPassword,
      inviterCode,
      showPassword,
      showConfirmPassword,
      loading,
      twoFactorChallengeId,
      twoFactorCode,
      handleSubmit,
      // 用户协议相关
      agreedToTerms,
      showTermsDialog,
      showPrivacyDialog,
      acceptTerms,
      acceptPrivacy,
      checkTermsDialog,
      hasShownTerms,
      hasReadTerms,
      hasReadPrivacy,
      termsContentRef,
      privacyContentRef,
      handleTermsScroll,
      handlePrivacyScroll,
      showPrivacy,
      turnstileContainerRef,
      turnstileError,
      turnstileEnabled,
    }
  }
}
</script>

<style scoped>
.login-page {
  padding: 100px 0;
  min-height: 100vh;
  display: flex;
  align-items: center;
  position: relative;
  overflow: hidden;
}

.login-page::before {
  content: '';
  position: absolute;
  top: -10%;
  right: -10%;
  width: 60%;
  height: 60%;
  background: radial-gradient(circle, rgba(68, 97, 242, 0.05) 0%, rgba(68, 97, 242, 0) 70%);
  z-index: -1;
}

.login-page::after {
  content: '';
  position: absolute;
  bottom: -10%;
  left: -10%;
  width: 60%;
  height: 60%;
  background: radial-gradient(circle, rgba(56, 191, 255, 0.05) 0%, rgba(56, 191, 255, 0) 70%);
  z-index: -1;
}

.auth-container {
  max-width: 480px;
  margin: 0 auto;
  width: 100%;
}

.auth-card {
  padding: 40px;
  background-color: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(10px);
  box-shadow: 0 16px 30px rgba(15, 23, 42, 0.1);
  border: 1px solid rgba(226, 232, 240, 0.8);
}

.auth-header {
  text-align: center;
  margin-bottom: 32px;
}

.auth-title {
  font-size: 32px;
  font-weight: 700;
  margin-bottom: 12px;
  color: var(--text-primary);
}

.auth-subtitle {
  font-size: 16px;
  color: var(--text-secondary);
}

.auth-tabs {
  display: flex;
  border-bottom: 1px solid var(--border-color);
  margin-bottom: 32px;
}

.auth-tab {
  flex: 1;
  padding: 16px;
  text-align: center;
  font-size: 16px;
  font-weight: 600;
  color: var(--text-secondary);
  background: none;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
  position: relative;
}

.auth-tab.active {
  color: var(--primary-color);
}

.auth-tab.active::after {
  content: '';
  position: absolute;
  bottom: -1px;
  left: 0;
  width: 100%;
  height: 2px;
  background: linear-gradient(90deg, #4461F2 0%, #38BFFF 100%);
}

.auth-form {
  margin-bottom: 32px;
}

.password-input-container,
.code-input-container {
  position: relative;
}

.password-toggle {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  color: var(--text-tertiary);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
}

.password-toggle:hover {
  color: var(--text-secondary);
}

.code-btn {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: var(--primary-color);
  font-weight: 600;
  cursor: pointer;
  padding: 8px 12px;
  white-space: nowrap;
}

.code-btn:disabled {
  color: var(--text-tertiary);
  cursor: not-allowed;
}

.form-actions {
  margin-top: 32px;
}

.turnstile-group {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.turnstile-widget {
  min-height: 65px;
}

.turnstile-error {
  margin: 8px 0 0;
  color: #F56C6C;
  font-size: 13px;
}

.btn-full {
  width: 100%;
}

.auth-divider {
  display: flex;
  align-items: center;
  margin: 32px 0;
}

.auth-divider::before,
.auth-divider::after {
  content: '';
  flex: 1;
  height: 1px;
  background-color: var(--border-color);
}

.auth-divider span {
  padding: 0 16px;
  color: var(--text-tertiary);
  font-size: 14px;
}

.social-login {
  display: flex;
  gap: 16px;
}

.social-btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px;
  border-radius: var(--radius-sm);
  background-color: var(--hover-color);
  border: 1px solid var(--border-color);
  color: var(--text-primary);
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.social-btn:hover {
  background-color: var(--card-background);
  transform: translateY(-2px);
}

@media (max-width: 768px) {
  .login-page {
    padding: 60px 0 40px;
  }
  
  .auth-container {
    margin: 0 16px;
  }
  
  .auth-card {
    padding: 30px 24px;
  }
  
  .auth-title {
    font-size: 28px;
  }
  
  .auth-subtitle {
    font-size: 15px;
  }
  
  .social-login {
    flex-direction: column;
  }
  
  .terms-content {
    max-height: 50vh;
    font-size: 13px;
  }
}

@media (max-width: 480px) {
  .login-page {
    padding: 40px 0 20px;
    min-height: calc(100vh - 60px);
  }
  
  .auth-container {
    margin: 0 12px;
    max-width: none;
  }
  
  .auth-card {
    padding: 24px 20px;
    background-color: rgba(255, 255, 255, 0.95);
    border-radius: 12px;
    box-shadow: 0 8px 25px rgba(15, 23, 42, 0.12);
  }
  
  .auth-header {
    margin-bottom: 24px;
  }
  
  .auth-title {
    font-size: 24px;
    margin-bottom: 8px;
  }
  
  .auth-subtitle {
    font-size: 14px;
  }
  
  .auth-tabs {
    margin-bottom: 24px;
  }
  
  .auth-tab {
    padding: 12px;
    font-size: 15px;
  }
  
  .form-group {
    margin-bottom: 16px;
  }
  
  .form-label {
    font-size: 14px;
    margin-bottom: 6px;
  }
  
  .form-input {
    padding: 12px 16px;
    font-size: 16px; /* 防止iOS缩放 */
    border-radius: 8px;
  }
  
  .password-input-container .form-input,
  .code-input-container .form-input {
    padding-right: 50px;
  }
  
  .password-toggle {
    right: 16px;
    padding: 4px;
  }
  
  .code-btn {
    right: 12px;
    padding: 6px 10px;
    font-size: 13px;
  }
  
  .btn {
    padding: 14px 20px;
    font-size: 16px;
    border-radius: 8px;
    font-weight: 600;
  }
  
  .btn-full {
    width: 100%;
  }
  
  .form-actions {
    margin-top: 24px;
  }
  
  .auth-divider {
    margin: 24px 0;
  }
  
  .auth-divider span {
    font-size: 13px;
    padding: 0 12px;
  }
  
  .social-btn {
    padding: 14px 16px;
    font-size: 15px;
    border-radius: 8px;
  }
  
  .agreement-group {
    margin-top: 12px;
  }
  
  .agreement-label {
    font-size: 13px;
    line-height: 1.4;
  }
  
  .terms-content {
    max-height: 40vh;
    padding: 0 12px;
    font-size: 13px;
  }
  
  .terms-content h2 {
    font-size: 18px;
    margin-bottom: 16px;
  }
  
  .terms-content h3 {
    font-size: 15px;
    margin: 16px 0 8px;
  }
  
  .dialog-footer {
    gap: 8px;
    margin-top: 12px;
  }
  
  .dialog-footer .btn {
    padding: 10px 16px;
    font-size: 14px;
  }
}

@media (max-width: 360px) {
  .auth-container {
    margin: 0 8px;
  }
  
  .auth-card {
    padding: 20px 16px;
  }
  
  .auth-title {
    font-size: 22px;
  }
  
  .form-input {
    padding: 11px 14px;
    font-size: 16px;
  }
  
  .password-input-container .form-input,
  .code-input-container .form-input {
    padding-right: 45px;
  }
  
  .password-toggle {
    right: 12px;
  }
  
  .code-btn {
    right: 8px;
    padding: 4px 8px;
    font-size: 12px;
  }
  
  .btn {
    padding: 12px 16px;
    font-size: 15px;
  }
  
  .terms-content {
    padding: 0 8px;
    font-size: 12px;
  }
}

/* 用户协议相关样式 */
.agreement-group {
  margin-top: 16px;
}

.checkbox-wrapper {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.agreement-label {
  font-size: 14px;
  color: var(--text-secondary);
  cursor: pointer;
  line-height: 1.5;
}

.agreement-label a {
  color: var(--primary-color);
  text-decoration: none;
}

.agreement-label a:hover {
  text-decoration: underline;
}

.terms-content {
  max-height: 60vh;
  overflow-y: auto;
  padding: 0 16px;
  color: var(--text-primary);
  font-size: 14px;
  line-height: 1.6;
}

.terms-content h2 {
  font-size: 20px;
  font-weight: 700;
  margin-bottom: 24px;
  color: var(--text-primary);
}

.terms-content h3 {
  font-size: 16px;
  font-weight: 600;
  margin: 24px 0 12px;
  color: var(--text-primary);
}

.terms-content p, .terms-content ul {
  margin-bottom: 12px;
}

.terms-content ul {
  padding-left: 24px;
}

.terms-content li {
  margin-bottom: 8px;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 16px;
}

:deep(.el-dialog__body) {
  padding: 20px 0;
}

/* 修改滚动条样式 */
.terms-content::-webkit-scrollbar {
  width: 8px;
}

.terms-content::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.terms-content::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 4px;
}

.terms-content::-webkit-scrollbar-thumb:hover {
  background: #555;
}

/* 添加底部提示 */
.terms-content::after {
  content: attr(data-scroll-hint);
  display: block;
  text-align: center;
  padding: 20px 0;
  color: var(--primary-color);
  font-weight: 500;
}
</style>
